import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from '../config/apiConfig';
import { useAuth } from '../components/router/authContext';
import {
  FaUser, FaBell, FaShieldAlt, FaBox, FaMoneyCheckAlt,
  FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCcDinersClub, FaCcJcb, FaHome,
} from 'react-icons/fa';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { LoadingSpinner } from "../components/general/LoadingSpinner";
import ProgressBar from "../components/general/ProgressBar";
import { logger } from '../utils/logger';

const InputField = ({ id, label, value, onChange, disabled, ...props }) => (
  <div>
    <label htmlFor={id} className="block mb-2 text-sm font-medium text-slate-700">{label}</label>
    <input
      id={id}
      name={id}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      className="w-full pl-4 pr-3.5 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 focus:bg-white disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed transition"
      {...props}
    />
  </div>
);

const ToggleSwitch = ({ id, label, description, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between py-4 border-b border-slate-200">
    <div>
      <h3 className="font-medium text-slate-800">{label}</h3>
      <p className="text-sm text-slate-500">{description}</p>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        id={id}
        name={id}
        checked={checked || false}
        onChange={onChange}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-200 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-700"></div>
    </label>
  </div>
);

const FormActions = ({ loading, onCancel }) => (
  <div className="flex items-center gap-4 mt-6">
    <button type="submit" className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-700 rounded-lg hover:bg-blue-800 disabled:opacity-50" disabled={loading}>
      {loading ? 'Saving...' : 'Save Changes'}
    </button>
    <button type="button" className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200" onClick={onCancel}>
      Cancel
    </button>
  </div>
);

const Section = ({ title, description, children }) => (
  <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
    <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
    {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
    <div className="mt-6">{children}</div>
  </div>
);

const NavItem = ({ label, icon, active, onClick }) => (
  <li>
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${active ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-600 hover:bg-slate-100 border border-transparent'}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  </li>
);

function Profile() {
  const [collapsed, setCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showDeleteAccountConfirmation, setShowDeleteAccountConfirmation] = useState(false);
  const [deleteAccountConfirmChecked, setDeleteAccountConfirmChecked] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);

  const { logout, refreshUserData, currentUser } = useAuth();
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");

  const [contentRef, isContentVisible] = useScrollAnimation({ triggerOnce: true, delay: 150 });

  // Use currentUser from AuthContext instead of local userData
  const userData = currentUser;

  const cancellationConfirmationWindow = () => {
    return (
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[9999] animate-fade-in"
        style={{ margin: 0, padding: 0 }}
        onClick={(e) => {
          // Close modal if clicking on backdrop
          if (e.target === e.currentTarget) {
            setShowCancelConfirmation(false);
          }
        }}
      >
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xl max-w-md mx-4 transform transition-all duration-300 ease-out animate-scale-in">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Cancel Subscription</h2>
          <p className="text-slate-600 mb-6">Are you sure you want to cancel your subscription? This action cannot be undone.</p>
          <div className="flex gap-4 justify-end">
            <button
              className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors"
              onClick={() => setShowCancelConfirmation(false)}
            >
              Never mind
            </button>
            <button
              className="px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              onClick={() => {
                handleCancelSubscription();
                setShowCancelConfirmation(false);
              }}
            >
              Yes, I'm sure
            </button>
          </div>
        </div>
      </div>
    )
  }

  const deleteAccountConfirmationWindow = () => {
    return (
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[9999] animate-fade-in"
        style={{ margin: 0, padding: 0 }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !deletingAccount) {
            setShowDeleteAccountConfirmation(false);
            setDeleteAccountConfirmChecked(false);
          }
        }}
      >
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xl max-w-xl mx-4 transform transition-all duration-300 ease-out animate-scale-in">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Delete Account</h2>
          <p className="text-slate-700 mb-3">
            This action is permanent for account access. Your account will be deactivated immediately and any active subscription will be cancelled immediately.
          </p>
          {/* <p className="text-slate-700 mb-3">
            Your data will not be erased instantly. It will be handled according to AmpTrans data management rules, and an internal deidentification process will be scheduled within 90 days.
          </p> */}
          <p className="text-red-700 font-semibold mb-6">
            You will be logged out immediately after confirmation.
          </p>
          <label className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={deleteAccountConfirmChecked}
              onChange={(e) => setDeleteAccountConfirmChecked(e.target.checked)}
              disabled={deletingAccount}
            />
            <span className="text-sm text-slate-700">
              I understand this will immediately deactivate my account and cancel billing.
            </span>
          </label>
          <div className="flex gap-4 justify-end">
            <button
              className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-60"
              onClick={() => {
                setShowDeleteAccountConfirmation(false);
                setDeleteAccountConfirmChecked(false);
              }}
              disabled={deletingAccount}
            >
              Keep my account
            </button>
            <button
              className="px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
              onClick={handleDeleteAccount}
              disabled={!deleteAccountConfirmChecked || deletingAccount}
            >
              {deletingAccount ? "Deleting..." : "Yes, delete my account"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (currentUser) {
      setFormData(currentUser);
      setLoading(false);
    } else if (!currentUser && !loading) {
      // If no current user and not loading, redirect to login
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showCancelConfirmation || showDeleteAccountConfirmation) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCancelConfirmation, showDeleteAccountConfirmation]);

  useEffect(() => {
    if (activeSection === 'subscriptions') {
      fetchPaymentMethods();
    }
  }, [activeSection, authToken, currentUser?.has_stripe_customer]);

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'email' || name === 'phone_number' || name === 'company_name') {
      return;
    }
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        notification_preferences: { ...prev.notification_preferences, [name]: checked }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeSection === 'personal') {
      return;
    }
    setLoading(true);
    axios.post(API_ENDPOINTS.updateProfile, formData, {
      headers: { Authorization: `Token ${authToken}` },
    })
      .then(response => {
        refreshUserData(); // Refresh user data from AuthContext
        setSuccessMessage('Profile updated successfully!');
        setIsEditing(false);
      })
      .catch(error => {
        setErrorMessage('Failed to update profile. Please try again.');
        logger.error(error);
      })
      .finally(() => setLoading(false));
  };

  const handleCancel = () => {
    setFormData(currentUser);
    setIsEditing(false);
  };

  const handleCancelSubscription = () => {
    if (currentUser.has_stripe_customer && currentUser.subscription_status === 'active') {
      axios.post(API_ENDPOINTS.cancelSubscription, {}, {
        headers: { Authorization: `Token ${authToken}` }
      })
        .then(response => {
          setSuccessMessage('Subscription cancelled successfully!');
          refreshUserData(); // Refresh user data to update the UI
        })
        .catch(error => {
          setErrorMessage('Failed to cancel subscription. Please try again.');
          logger.error(error);
        });
    }
    else {
      setErrorMessage('You do not have an active subscription.');
    }
  };

  const handleResendVerifyEmail = () => {
    axios.post(API_ENDPOINTS.resendVerificationEmail, {}, {
      headers: { Authorization: `Token ${authToken}` }
    })
      .then(response => {
        setSuccessMessage('Please check your email for a verification link.');
      })
      .catch(error => {
        setErrorMessage('Failed to send verification email. Please try again.');
        logger.error(error);
      });
  };

  const handleDeleteAccount = () => {
    if (!deleteAccountConfirmChecked || deletingAccount) {
      return;
    }

    setDeletingAccount(true);
    axios.post(API_ENDPOINTS.deleteAccount, {}, {
      headers: { Authorization: `Token ${authToken}` }
    })
      .then(() => {
        logout();
        navigate('/login');
      })
      .catch((error) => {
        setErrorMessage(error.response?.data?.error || 'Failed to delete account. Please try again.');
        logger.error(error);
      })
      .finally(() => {
        setDeletingAccount(false);
        setShowDeleteAccountConfirmation(false);
        setDeleteAccountConfirmChecked(false);
      });
  };

  const handleSyncWithStripe = () => {
    axios.post(API_ENDPOINTS.syncWithStripe, {}, {
      headers: { Authorization: `Token ${authToken}` }
    })
      .then(response => {
        setSuccessMessage('Synced with Stripe successfully!');
      })
      .catch(error => {
        setErrorMessage('Failed to sync with Stripe. Please try again.');
        logger.error(error);
      });
  };

  function fetchPaymentMethods() {
    if (!authToken || !currentUser?.has_stripe_customer) {
      setPaymentMethods([]);
      return;
    }
    setPaymentMethodsLoading(true);
    axios.get(API_ENDPOINTS.paymentMethods, {
      headers: { Authorization: `Token ${authToken}` }
    })
      .then((response) => {
        setPaymentMethods(response.data?.payment_methods || []);
      })
      .catch((error) => {
        logger.error('Failed to fetch payment methods', error);
        setPaymentMethods([]);
      })
      .finally(() => setPaymentMethodsLoading(false));
  }

  const handleSetDefaultPaymentMethod = (paymentMethodId) => {
    axios.post(
      API_ENDPOINTS.setDefaultPaymentMethod,
      { payment_method_id: paymentMethodId },
      { headers: { Authorization: `Token ${authToken}` } }
    )
      .then(() => {
        setSuccessMessage('Default payment method updated.');
        fetchPaymentMethods();
        refreshUserData();
      })
      .catch((error) => {
        setErrorMessage(error.response?.data?.error || 'Failed to update default payment method.');
        logger.error(error);
      });
  };

  const renderPersonalInfo = () => (
    <Section
      title="Personal Information"
      description="Email and company details are locked to the account you signed up with. They cannot be edited here."
    >
      <div className="mt-6 space-y-6">
        <InputField id="email" label="Email Address" type="email" value={formData.email} disabled required readOnly />
        <InputField id="phone_number" label="Phone Number" type="tel" value={formData.phone_number} disabled readOnly />
        <InputField id="company_name" label="Company Name" value={formData.company_name} disabled readOnly />
      </div>
    </Section>
  );

  const renderNotifications = () => (
    <Section title="Notification Preferences" description="">
      <form onSubmit={handleSubmit}>
        <ToggleSwitch id="email_notifications" label="Email Notifications" description="Receive important updates via email" checked={formData.notification_preferences?.email_notifications} onChange={handleInputChange} disabled={!isEditing} />
        <ToggleSwitch id="sms_notifications" label="SMS Notifications" description="Receive important updates via text message" checked={formData.notification_preferences?.sms_notifications} onChange={handleInputChange} disabled={!isEditing} />
        <ToggleSwitch id="weekly_reports" label="Weekly Reports" description="Receive weekly fleet summary reports" checked={formData.notification_preferences?.weekly_reports} onChange={handleInputChange} disabled={!isEditing} />
        <ToggleSwitch id="marketing_emails" label="Marketing Emails" description="Receive new feature and promotion updates" checked={formData.notification_preferences?.marketing_emails} onChange={handleInputChange} disabled={!isEditing} />
        {isEditing ? <FormActions loading={loading} onCancel={handleCancel} /> : <button type="button" className="mt-6 px-5 py-2.5 text-sm font-semibold text-white bg-blue-700 rounded-lg hover:bg-blue-800" onClick={() => setIsEditing(true)}>Edit Preferences</button>}
      </form>
    </Section>
  );

  const renderAccountSettings = () => {
    return (
      <Section title="Account Settings" description="">
        <div className="overflow-hidden bg-white border border-slate-200 rounded-xl">
          <ul className="divide-y divide-slate-200">
            <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-slate-600">Username</span><span>{userData.username}</span></li>
            <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-slate-600">Verified Email</span><span className="text-slate-800">{userData.email_verified ? 'Verified' : 'Unverified'}</span></li>
          </ul>
        </div>
        {!userData.email_verified && (
          <button className="mt-6 px-5 py-2.5 text-sm font-semibold text-white bg-blue-700 rounded-lg hover:bg-blue-800" onClick={() => handleResendVerifyEmail(true)}>Resend Verification Email</button>
        )}
        <button
            className="mt-4 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700"
            onClick={() => {
              setDeleteAccountConfirmChecked(false);
              setShowDeleteAccountConfirmation(true);
            }}
          >
            Delete Account
        </button>
      </Section>
    );
  };

  const renderProducts = () => {
    return (
      <Section title="Your Products" description="Manage your products and their details.">
        <div>
          <h2>Your Products</h2>
        </div>
      </Section>
    );
  };

  const renderSubscriptions = () => {
    const StatusBadge = ({ status }) => (
      <span className={`px-2.5 py-1 text-xs font-semibold leading-tight rounded-full border ${status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
        {status}
      </span>
    );
    return (
      <Section title="Manage Subscriptions" description="">
        <div>
          <h2 className="text-lg font-bold text-slate-700 mt-6 py-2">AmpTrans eFleetPlan</h2>
          <div className="overflow-hidden bg-white border border-slate-200 rounded-xl">
            <ul className="divide-y divide-slate-200">
              <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-slate-600">Subscription Type</span><span className="font-medium capitalize text-slate-800">{userData.subscription_type}</span></li>
              <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-slate-600">Status</span><StatusBadge status={userData.subscription_status} /></li>
              {
                userData.subscription_status === 'cancelled' ? (
                  <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-slate-600">Expires</span><span className="text-slate-800">{new Date(userData.subscription_end_date).toLocaleDateString()}</span></li>
                )
                  : (
                    <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-slate-600">Renewal Date</span><span className="text-slate-800">{userData.subscription_renewal_date ? new Date(userData.subscription_renewal_date).toLocaleDateString() : 'N/A'}</span></li>
                  )
              }
            </ul>
          </div>
          {
            userData.subscription_status === 'active' && (
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="px-5 py-2.5 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100" onClick={() => setShowCancelConfirmation(true)}>Cancel Subscription</button>
                <button className="px-5 py-2.5 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100" onClick={() => navigate('/subscription')}>Manage Plan</button>
              </div>
            )
          }
          {
            userData.subscription_status === 'cancelled' && (
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="px-5 py-2.5 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100" onClick={() => navigate('/subscription')}>Reactivate / Change Plan</button>
              </div>
            )
          }
        </div>

        {/* Usage Details Section */}
        <div>
          <h2 className="text-lg font-bold text-slate-700 mt-6 py-2">Usage Details</h2>
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
            {/* Routes Usage */}
            <ProgressBar
              label="Routes"
              current={userData.total_route_usage}
              max={userData.route_usage_limit}
              unit="routes"
              color="blue"
              className="mb-4"
              showPercentage={false}
            />

            {/* Depots Usage */}
            <ProgressBar
              label="Depots"
              current={userData.total_depot_usage}
              max={userData.depot_usage_limit}
              unit="depots"
              color="green"
              className="mb-4"
              showPercentage={false}
            />

            {/* Monthly Runs Usage */}
            <ProgressBar
              label="Optimization Runs (This Month)"
              current={userData.optimization_runs_this_month}
              max={userData.optimization_usage_limit}
              unit="runs"
              color="purple"
              className="mb-4"
              showPercentage={false}
            />

            {/* Plan upgrade notice */}
            {(userData.total_route_usage / userData.route_usage_limit >= 0.9 || 
              userData.total_depot_usage / userData.depot_usage_limit >= 0.9 || 
              userData.optimization_runs_this_month / userData.optimization_usage_limit >= 0.9) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      Approaching Plan Limits
                    </h3>
                    <p className="mt-1 text-sm text-amber-700">
                      You're approaching your plan limits. Consider upgrading to avoid service interruptions.
                    </p>
                    <div className="mt-3">
                      <a 
                        href="/subscription" 
                        className="text-sm font-medium text-amber-800 hover:text-amber-900 underline"
                      >
                        Upgrade Plan →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-bold text-slate-700 mt-6 py-2">Payment Details</h2>
          <div className="overflow-hidden bg-white border border-slate-200 rounded-xl">
            <ul className="divide-y divide-slate-200">
              {paymentMethodsLoading && (
                <li className="px-6 py-4 text-sm text-slate-500">Loading payment methods...</li>
              )}
              {!paymentMethodsLoading && paymentMethods.length === 0 && (
                <li className="px-6 py-4 text-sm text-slate-500">
                  {userData.last4 ? `**** **** **** ${userData.last4}` : 'No payment method on file.'}
                </li>
              )}
              {!paymentMethodsLoading && paymentMethods.map((method) => (
                <li key={method.id} className="px-6 py-4 flex justify-between items-center gap-4">
                  <div className="flex items-center gap-3">
                    <span className="font-medium capitalize text-slate-800">
                      **** **** **** {method.card?.last4 || '----'}
                    </span>
                    <span className="text-slate-700">
                      {method.card?.exp_month}/{String(method.card?.exp_year || '').slice(-2)}
                    </span>
                    {method.card?.brand === 'visa' && <FaCcVisa className="w-8 h-auto text-blue-700" />}
                    {method.card?.brand === 'mastercard' && <FaCcMastercard className="w-8 h-auto" />}
                    {method.card?.brand === 'amex' && <FaCcAmex className="w-8 h-auto" />}
                    {method.card?.brand === 'discover' && <FaCcDiscover className="w-8 h-auto" />}
                    {method.card?.brand === 'diners' && <FaCcDinersClub className="w-8 h-auto" />}
                    {method.card?.brand === 'jcb' && <FaCcJcb className="w-8 h-auto" />}
                  </div>
                  {method.is_default ? (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1">
                      Default
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="text-xs font-semibold text-blue-700 hover:underline"
                      onClick={() => handleSetDefaultPaymentMethod(method.id)}
                    >
                      Set as default
                    </button>
                  )}
                </li>
              ))}
              <li className="px-6 py-4 flex justify-between items-center"><span className="font-medium text-slate-600">Upcoming Payment Amount</span> <span className="font-medium text-slate-800">{userData.subscription_payment_amount ? `$${userData.subscription_payment_amount}` : '--'}</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>
    );
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'personal': return renderPersonalInfo();
      case 'notifications': return renderNotifications();
      case 'account': return renderAccountSettings();
      case 'products': return renderProducts();
      case 'subscriptions': return renderSubscriptions();
      default: return renderPersonalInfo();
    }
  };

  return (
    <div className="relative flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30">
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #1e3a5f 1px, transparent 0)', backgroundSize: '32px 32px' }}
      />
      <div className="fixed top-0 right-0 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-[400px] h-[400px] bg-teal-100/40 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />
      {/* Root level modals for entire */}
      {showCancelConfirmation && cancellationConfirmationWindow()}
      {showDeleteAccountConfirmation && deleteAccountConfirmationWindow()}

      <main className="relative z-10 flex-1">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <div className="mb-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-700 mb-2">Profile</p>
            <h1 className="text-3xl font-bold text-slate-900">{userData.first_name} {userData.last_name}</h1>
          </div>

          {successMessage && <div className="mb-6 p-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg animate-fade-in">{successMessage}</div>}
          {errorMessage && <div className="mb-6 p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg animate-fade-in">{errorMessage}</div>}

          <div
            ref={contentRef}
            className={`grid grid-cols-1 gap-10 lg:grid-cols-4 transition-all duration-700 ease-out ${isContentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <aside className="lg:col-span-1">
              <nav className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
                <ul className="space-y-1">
                  <NavItem label="Personal Info" icon={<FaUser />} active={activeSection === 'personal'} onClick={() => { setActiveSection('personal'); setIsEditing(false); }} />
                  <NavItem label="Notifications" icon={<FaBell />} active={activeSection === 'notifications'} onClick={() => { setActiveSection('notifications'); setIsEditing(false); }} />
                  <NavItem label="Account" icon={<FaShieldAlt />} active={activeSection === 'account'} onClick={() => { setActiveSection('account'); setIsEditing(false); }} />
                </ul>
                <ul className="pt-4">
                  {/* <NavItem id="products" label="Your Products" icon={<FaBox />} /> */}
                  <NavItem label="Subscriptions" icon={<FaMoneyCheckAlt />} active={activeSection === 'subscriptions'} onClick={() => { setActiveSection('subscriptions'); setIsEditing(false); }} />
                </ul>
                <ul className="pt-8">
                  <Link to="/home" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors text-slate-600 hover:bg-slate-100">
                    <FaHome />
                    <span>Back to home</span>
                  </Link>
                </ul>
              </nav>
            </aside>

            <div className="lg:col-span-3">
              {renderActiveSection()}
            </div>
          </div>
          {/* For testing purposes only */}
          {/* <button className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => handleSyncWithStripe()}>Sync with Stripe</button> */}
        </div>
      </main>
    </div>
  );
}

export default Profile;
